import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';
import './InteractiveLegalDiagram.css';

// Comprehensive legal node data with expandable sub-nodes
const legalNodeData = {
  'constitution': {
    title: 'U.S. Constitution (1787)',
    description: 'Supreme law establishing government framework and fundamental rights',
    examples: ['Article I: Legislative', 'Article II: Executive', 'Article III: Judicial', 'Supremacy Clause', 'Separation of Powers']
  },
  'billOfRights': {
    title: 'Bill of Rights (1791)',
    description: 'First 10 amendments protecting individual liberties',
    examples: ['1st: Speech/Religion', '4th: Search/Seizure', '5th: Due Process', '6th: Fair Trial', '8th: No Cruel Punishment']
  },
  'amendments': {
    title: 'Amendments 11-27',
    description: 'Subsequent amendments addressing evolving rights',
    examples: ['13th: Abolish Slavery', '14th: Equal Protection', '15th: Voting Rights', '19th: Women Suffrage', '26th: Age 18']
  },
  'civilRights': {
    title: 'Civil Rights Laws',
    description: 'Prohibiting discrimination and protecting equal rights',
    examples: ['Civil Rights Act 1964', 'Voting Rights Act 1965', 'Fair Housing 1968', 'ADA 1990', 'Title IX 1972'],
    expandable: true
  },
  'civilRightsAct1964': {
    title: 'Civil Rights Act of 1964',
    description: 'Landmark law prohibiting discrimination',
    examples: ['Title II: Public Accommodations', 'Title VII: Employment', 'Title VI: Federal Funding', 'Created EEOC', 'Prohibits race/sex discrimination'],
    expandable: true
  },
  'civilRightsTitleII': {
    title: 'Title II - Public Accommodations',
    description: 'Prohibits discrimination in public places',
    examples: ['Hotels/motels', 'Restaurants', 'Entertainment venues', 'Interstate commerce', 'Commerce Clause basis']
  },
  'civilRightsTitleVII': {
    title: 'Title VII - Employment',
    description: 'Prohibits employment discrimination',
    examples: ['Race, color, religion, sex, national origin', 'Disparate treatment', 'Disparate impact', 'Reasonable accommodation', 'EEOC enforcement']
  },
  'civilRightsTitleVI': {
    title: 'Title VI - Federal Funding',
    description: 'No discrimination in federally funded programs',
    examples: ['Spending power basis', 'Educational institutions', 'Healthcare facilities', 'State/local programs', 'Private right of action']
  },
  'votingRights1965': {
    title: 'Voting Rights Act 1965',
    description: 'Protects voting rights, bans discriminatory practices',
    examples: ['Section 2: Discrimination ban', 'Section 5: Preclearance', 'Literacy test ban', 'Federal examiners', 'Language assistance']
  },
  'fairHousing': {
    title: 'Fair Housing Act 1968',
    description: 'Prohibits housing discrimination',
    examples: ['Race/color protection', 'Religion/national origin', 'Sex/familial status', 'Disability protections', 'HUD enforcement']
  },
  'ada': {
    title: 'Americans with Disabilities Act 1990',
    description: 'Prohibits disability discrimination',
    examples: ['Title I: Employment', 'Title II: Public Services', 'Title III: Public Accommodations', 'Accessibility requirements', 'Reasonable accommodations'],
    expandable: true
  },
  'adaTitleI': {
    title: 'Title I - Employment',
    description: 'Employment discrimination protection',
    examples: ['Qualified individual standard', 'Reasonable accommodation', 'Undue hardship defense', 'Interactive process', 'Essential functions']
  },
  'adaTitleII': {
    title: 'Title II - Public Services',
    description: 'State/local government services',
    examples: ['Program accessibility', 'Transportation', 'Courts', 'Polling places', 'Architectural barriers']
  },
  'adaTitleIII': {
    title: 'Title III - Public Accommodations',
    description: 'Private businesses open to public',
    examples: ['12 categories', 'Readily achievable', 'New construction standards', 'Alterations', 'Service animals']
  },
  'criminal': {
    title: 'Criminal Law',
    description: 'Federal criminal statutes defining crimes and penalties',
    examples: ['Title 18 U.S.C.', 'RICO Act', 'USA PATRIOT Act', 'Computer Fraud', 'Bank Secrecy Act'],
    expandable: true
  },
  'title18': {
    title: 'Title 18 U.S.C. - Crimes',
    description: 'Federal Criminal Code',
    examples: ['Murder/Manslaughter', 'Kidnapping', 'Robbery/Burglary', 'Fraud/Embezzlement', 'Obstruction of Justice']
  },
  'rico': {
    title: 'RICO Act 1970',
    description: 'Racketeer Influenced Corrupt Organizations',
    examples: ['Pattern of racketeering', 'Enterprise requirement', 'Treble damages', 'Criminal forfeiture', 'Organized crime prosecution']
  },
  'patriotAct': {
    title: 'USA PATRIOT Act 2001',
    description: 'Terrorism prevention and national security',
    examples: ['Enhanced surveillance', 'Roving wiretaps', 'Business records', 'Foreign intelligence', 'Material support'],
    expandable: true
  },
  'patriotActSurveillance': {
    title: 'Section 215 - Business Records',
    description: 'Allows FBI to obtain tangible things for terrorism investigations',
    examples: ['Court order required', 'Relevance standard', 'Bulk collection', 'NSA metadata program', 'USA FREEDOM reforms']
  },
  'patriotActWiretaps': {
    title: 'Section 206 - Roving Wiretaps',
    description: 'Multi-point wiretaps without specifying target',
    examples: ['Target not device-specific', 'Follows person', 'FISA court approval', 'National security cases', 'Ascertainment requirement']
  },
  'patriotActIntelligence': {
    title: 'Section 218 - FISA Intelligence',
    description: 'Lowers wall between intelligence and law enforcement',
    examples: ['Significant purpose test', 'Info sharing', 'Criminal prosecution use', 'Intelligence gathering', 'Minimization procedures']
  },
  'cfaa': {
    title: 'Computer Fraud & Abuse Act',
    description: 'Federal cybercrime statute',
    examples: ['Unauthorized access', 'Exceeding authorization', 'Password trafficking', 'Damage to computers', 'Espionage']
  },
  'commerce': {
    title: 'Commerce & Business',
    description: 'Regulating business practices and interstate commerce',
    examples: ['Sherman Act', 'Clayton Act', 'Sarbanes-Oxley', 'Dodd-Frank', 'FCPA'],
    expandable: true
  },
  'shermanAct': {
    title: 'Sherman Antitrust Act 1890',
    description: 'Prohibits monopolies and restraints on trade',
    examples: ['Section 1: Agreements', 'Section 2: Monopolization', 'Per se violations', 'Rule of reason', 'Criminal/civil penalties']
  },
  'claytonAct': {
    title: 'Clayton Act 1914',
    description: 'Prevents anti-competitive practices',
    examples: ['Price discrimination', 'Tying arrangements', 'Exclusive dealing', 'Mergers/acquisitions', 'Interlocking directorates']
  },
  'sarbanesOxley': {
    title: 'Sarbanes-Oxley 2002',
    description: 'Corporate financial reporting standards',
    examples: ['Section 302: CEO/CFO cert', 'Section 404: Internal controls', 'Section 806: Whistleblower', 'PCAOB creation', 'Audit independence'],
    expandable: true
  },
  'sox302': {
    title: 'Section 302 - CEO/CFO Certification',
    description: 'Executives must certify accuracy of financial statements',
    examples: ['Personal responsibility', 'Reviewed all reports', 'No material misstatements', 'Internal controls adequate', 'Criminal penalties']
  },
  'sox404': {
    title: 'Section 404 - Internal Controls',
    description: 'Assessment of internal control over financial reporting',
    examples: ['Management assessment', 'Auditor attestation', 'Material weakness disclosure', 'ICFR framework', 'Most costly provision']
  },
  'sox806': {
    title: 'Section 806 - Whistleblower Protection',
    description: 'Protects employees who report fraud',
    examples: ['Anti-retaliation', 'Private right of action', 'Reinstatement remedy', 'DOL administration', 'Extended SOL']
  },
  'doddFrank': {
    title: 'Dodd-Frank 2010',
    description: 'Financial industry regulation',
    examples: ['Volcker Rule', 'CFPB creation', 'Systemic risk oversight', 'Derivatives regulation', 'Executive compensation'],
    expandable: true
  },
  'volckerRule': {
    title: 'Section 619 - Volcker Rule',
    description: 'Prohibits proprietary trading by banks',
    examples: ['Ban on prop trading', 'Hedge fund limits', 'Private equity limits', 'Market making exception', 'Compliance program']
  },
  'cfpbCreation': {
    title: 'Title X - Consumer Financial Protection Bureau',
    description: 'New agency to protect consumers',
    examples: ['Consumer protection', 'Rulemaking authority', 'Supervision/enforcement', 'Fair lending', 'Single director']
  },
  'doddFrankSystemic': {
    title: 'Title I - FSOC & Systemic Risk',
    description: 'Financial Stability Oversight Council',
    examples: ['Systemically important', 'Enhanced prudential standards', 'Living wills', 'Stress tests', 'Orderly liquidation']
  },
  'labor': {
    title: 'Labor & Employment',
    description: 'Protecting workers and regulating employment',
    examples: ['FLSA', 'OSHA', 'FMLA', 'ERISA', 'NLRA'],
    expandable: true
  },
  'flsa': {
    title: 'Fair Labor Standards Act 1938',
    description: 'Minimum wage, overtime, child labor',
    examples: ['$7.25 minimum wage', '40-hour work week', 'Time-and-a-half OT', 'Child labor restrictions', 'Exempt vs non-exempt']
  },
  'osha': {
    title: 'OSHA 1970',
    description: 'Workplace safety and health standards',
    examples: ['General duty clause', 'Safety standards', 'Inspections/citations', 'Whistleblower protection', 'Training requirements']
  },
  'fmla': {
    title: 'FMLA 1993',
    description: 'Family and medical leave protections',
    examples: ['12 weeks unpaid leave', 'Birth/adoption', 'Serious health condition', 'Family care', 'Job protection']
  },
  'erisa': {
    title: 'ERISA 1974',
    description: 'Employee retirement and benefits',
    examples: ['Fiduciary duties', 'Vesting requirements', 'Funding standards', 'Disclosure rules', 'PBGC insurance']
  },
  'ip': {
    title: 'Intellectual Property',
    description: 'Protecting creative works and innovations',
    examples: ['Patents', 'Copyrights', 'Trademarks', 'Trade Secrets', 'DMCA'],
    expandable: true
  },
  'patents': {
    title: 'Patent Act - 35 U.S.C.',
    description: 'Protects inventions for 20 years',
    examples: ['Utility patents', 'Design patents', 'Plant patents', 'Novelty requirement', 'Non-obviousness']
  },
  'copyrights': {
    title: 'Copyright Act - 17 U.S.C.',
    description: 'Protects creative works',
    examples: ['Life + 70 years', 'Reproduction rights', 'Derivative works', 'Public performance', 'Fair use defense']
  },
  'trademarks': {
    title: 'Trademark Act (Lanham) 1946',
    description: 'Protects brand names and logos',
    examples: ['Distinctive marks', 'Likelihood of confusion', 'Dilution', 'Trade dress', 'USPTO registration']
  },
  'dmca': {
    title: 'DMCA 1998',
    description: 'Digital copyright protections',
    examples: ['Anti-circumvention', 'Safe harbor ISPs', 'Notice and takedown', 'WIPO treaties', 'DRM protection']
  },
  'environmental': {
    title: 'Environmental Law',
    description: 'Protecting natural resources and public health',
    examples: ['Clean Air', 'Clean Water', 'NEPA', 'ESA', 'CERCLA'],
    expandable: true
  },
  'cleanAir': {
    title: 'Clean Air Act 1970',
    description: 'Regulates air emissions and quality',
    examples: ['NAAQS standards', 'SIPs', 'New source review', 'Title V permits', 'Mobile source standards'],
    expandable: true
  },
  'naaqs': {
    title: 'NAAQS - National Ambient Air Quality Standards',
    description: 'Sets limits for criteria pollutants',
    examples: ['Six criteria pollutants', 'Primary standards (health)', 'Secondary standards (welfare)', 'Review every 5 years', 'Attainment/nonattainment']
  },
  'newSourceReview': {
    title: 'New Source Review (NSR)',
    description: 'Pre-construction permits for new/modified sources',
    examples: ['PSD for attainment areas', 'Nonattainment NSR', 'BACT requirement', 'Offsets', 'Major modifications']
  },
  'titleVPermits': {
    title: 'Title V - Operating Permits',
    description: 'Comprehensive operating permits for major sources',
    examples: ['All requirements in one permit', '5-year term', 'Annual fees', 'Enhanced monitoring', 'Public participation']
  },
  'cleanWater': {
    title: 'Clean Water Act 1972',
    description: 'Protects waters from pollution',
    examples: ['NPDES permits', 'Section 404 wetlands', 'Water quality standards', 'TMDLs', 'Point/non-point sources']
  },
  'nepa': {
    title: 'NEPA 1969',
    description: 'Environmental impact assessments',
    examples: ['EIS requirement', 'EA process', 'Public participation', 'CEQ regulations', 'Federal actions']
  },
  'esa': {
    title: 'Endangered Species Act 1973',
    description: 'Protects threatened wildlife',
    examples: ['Listing process', 'Critical habitat', 'Section 7 consultation', 'Section 9 take prohibition', 'Recovery plans']
  },
  'healthcare': {
    title: 'Healthcare Law',
    description: 'Regulating healthcare delivery and insurance',
    examples: ['Medicare', 'Medicaid', 'HIPAA', 'ACA', 'FDA'],
    expandable: true
  },
  'medicare': {
    title: 'Medicare 1965',
    description: 'Health insurance for 65+ and disabled',
    examples: ['Part A: Hospital', 'Part B: Medical', 'Part C: Advantage', 'Part D: Prescription', 'CMS administration']
  },
  'medicaid': {
    title: 'Medicaid 1965',
    description: 'Health coverage for low-income',
    examples: ['Federal-state partnership', 'Mandatory eligibility', 'Optional services', 'Expansion under ACA', 'Managed care']
  },
  'hipaa': {
    title: 'HIPAA 1996',
    description: 'Patient privacy and health information',
    examples: ['Privacy Rule', 'Security Rule', 'Breach notification', 'Business associates', 'Protected health info'],
    expandable: true
  },
  'hipaaPrivacy': {
    title: 'Privacy Rule - 45 CFR Part 160/164',
    description: 'Standards for protecting PHI',
    examples: ['PHI definition', 'Permitted uses/disclosures', 'Minimum necessary', 'Patient rights', 'Notice of privacy practices']
  },
  'hipaaSecurity': {
    title: 'Security Rule - 45 CFR Part 164',
    description: 'Safeguards for electronic PHI',
    examples: ['Administrative safeguards', 'Physical safeguards', 'Technical safeguards', 'Risk assessment', 'Encryption']
  },
  'hipaaBreach': {
    title: 'Breach Notification Rule',
    description: 'Requirements when PHI is breached',
    examples: ['Individual notification', 'HHS notification', 'Media notification (500+)', 'Risk assessment', 'Business associate duty']
  },
  'aca': {
    title: 'Affordable Care Act 2010',
    description: 'Health insurance marketplace and mandates',
    examples: ['Individual mandate', 'Employer mandate', 'Exchanges/marketplace', 'Pre-existing conditions', 'Essential benefits'],
    expandable: true
  },
  'acaIndividualMandate': {
    title: 'Individual Mandate - Section 1501',
    description: 'Requirement to maintain minimum essential coverage',
    examples: ['Tax penalty (zeroed 2019)', 'Exemptions available', 'Upheld as tax power', 'NFIB v. Sebelius', 'Affordable coverage test']
  },
  'acaExchanges': {
    title: 'Health Insurance Exchanges',
    description: 'Marketplaces for purchasing insurance',
    examples: ['State/federal exchanges', 'Premium subsidies', 'Cost-sharing reductions', 'Metal tiers (Bronze-Platinum)', 'Navigator programs']
  },
  'acaEssentialBenefits': {
    title: 'Essential Health Benefits',
    description: 'Required coverage categories',
    examples: ['10 benefit categories', 'Ambulatory services', 'Emergency services', 'Hospitalization', 'Maternity/mental health']
  },
  'financial': {
    title: 'Financial & Banking',
    description: 'Regulating financial institutions and securities',
    examples: ['Securities Act 1933', 'Exchange Act 1934', 'Fed Reserve', 'Investment Company Act', 'FINRA'],
    expandable: true
  },
  'securities1933': {
    title: 'Securities Act 1933',
    description: 'Regulates new securities offerings',
    examples: ['Registration requirement', 'Prospectus disclosure', 'Section 5 prohibitions', 'Exemptions', 'Liability provisions']
  },
  'exchange1934': {
    title: 'Securities Exchange Act 1934',
    description: 'Regulates securities trading and markets',
    examples: ['SEC creation', '10-K/10-Q filing', 'Proxy rules', 'Insider trading', 'Market manipulation']
  },
  'fedReserve': {
    title: 'Federal Reserve Act 1913',
    description: 'Created central banking system',
    examples: ['12 Reserve Banks', 'FOMC', 'Monetary policy', 'Bank supervision', 'Payment systems']
  },
  'investmentCompany': {
    title: 'Investment Company Act 1940',
    description: 'Regulates mutual funds and investment companies',
    examples: ['Registration requirement', 'Diversification rules', 'Board requirements', 'Valuation standards', 'Custody rules']
  },
  'adminLaw': {
    title: 'Administrative & Regulatory Law',
    description: 'Rules created by federal agencies under statutory authority',
    examples: ['CFR', 'EPA', 'FDA', 'SEC', 'FCC'],
    expandable: true
  },
  'caselaw': {
    title: 'Federal Case Law',
    description: 'Legal precedents established by federal court decisions',
    examples: ['Supreme Court', 'Circuit Courts', 'District Courts', 'Binding precedent', 'Stare decisis'],
    expandable: true
  },
  'cfr': {
    title: 'Code of Federal Regulations',
    description: '50 titles of agency rules',
    examples: ['Notice and comment', 'APA compliance', 'Federal Register', 'Judicial review', 'Chevron deference']
  },
  'epaRegs': {
    title: 'EPA Regulations',
    description: 'Environmental standards and permits',
    examples: ['40 CFR', 'Emission standards', 'Effluent limits', 'Hazardous waste', 'TSCA chemicals']
  },
  'fdaRegs': {
    title: 'FDA Regulations',
    description: 'Food safety and drug approval',
    examples: ['21 CFR', 'Drug approval process', 'Clinical trials', 'Food safety', 'Medical devices']
  },
  'secRegs': {
    title: 'SEC Regulations',
    description: 'Securities trading and disclosure',
    examples: ['Reg D offerings', 'Reg S offshore', 'Reg A+ mini-IPO', 'Form S-1', 'Form 10-K']
  },
  'supremeCourt': {
    title: 'Supreme Court',
    description: 'Highest federal court, final interpreter',
    examples: ['9 Justices', 'Judicial review', 'Certiorari', 'Binding precedent', '~70 cases/year']
  },
  'circuitCourts': {
    title: 'Circuit Courts',
    description: '13 appellate courts',
    examples: ['12 regional + Federal', '3-judge panels', 'Circuit precedent', 'En banc review', 'Appeal of right']
  },
  'districtCourts': {
    title: 'District Courts',
    description: '94 trial courts',
    examples: ['Original jurisdiction', 'Single judge', 'Jury trials', 'Federal crimes', 'Civil cases']
  },
  'stateLaw': {
    title: 'State Law Systems',
    description: 'Each state has own legal system',
    examples: ['State constitutions', 'State statutes', 'State courts', 'Police powers', 'UCC adoption'],
    expandable: true
  },
  'stateConstitutions': {
    title: 'State Constitutions',
    description: 'Fundamental law of each state',
    examples: ['Cannot conflict with federal', 'Greater rights possible', 'State government structure', 'Amendment processes', 'California environmental rights']
  },
  'stateStatutes': {
    title: 'State Statutes',
    description: 'Laws on matters of state concern',
    examples: ['Criminal law (most)', 'Family law', 'Property law', 'Contract law', 'Tort law']
  },
  'uniformCodes': {
    title: 'Uniform Codes',
    description: 'Model legislation for consistency',
    examples: ['UCC (all states)', 'Model Penal Code', 'Uniform Probate Code', 'Partnership Act', 'Child Custody Act']
  },
  'federalStatutes': {
    title: 'Federal Statutory Law',
    description: 'Laws enacted by Congress, codified in the United States Code (U.S.C.)',
    examples: ['Organized into 54 titles by subject matter', 'Examples: Civil Rights Act, Clean Air Act, Tax Code', 'Must comply with Constitutional provisions', 'Can be amended or repealed by Congress', 'Subject to judicial review by courts'],
    expandable: true
  },
  'international': {
    title: 'International Law',
    description: 'Treaties binding the United States',
    examples: ['UN Charter', 'Geneva Conventions', 'Trade agreements', 'Human rights treaties', 'Extradition']
  }
};

function InteractiveLegalDiagram() {
  const mermaidRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [expandedNodes, setExpandedNodes] = useState({
    // Level 1 - Top categories
    federalStatutes: false,
    caselaw: false,
    stateLaw: false,
    // Level 2 - Main categories
    civilRights: false,
    criminal: false,
    commerce: false,
    labor: false,
    ip: false,
    environmental: false,
    healthcare: false,
    financial: false,
    adminLaw: false,
    // Level 3 - Individual laws
    civilRightsAct1964: false,
    ada: false,
    patriotAct: false,
    sarbanesOxley: false,
    doddFrank: false,
    cleanAir: false,
    hipaa: false,
    aca: false
  });

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  }, []);

  const generateMermaidDiagram = useCallback(() => {
    let diagram = `
      graph TD
        constitution[U.S. Constitution<br/>1787]
        billOfRights[Bill of Rights<br/>1791]
        amendments[Amendments<br/>11-27]
        federalStatutes[⊞ Federal Statutory<br/>Law]
        caselaw[⊞ Federal Case<br/>Law]
        stateLaw[⊞ State Law<br/>Systems]
        international[International &<br/>Treaty Law]

        constitution --> billOfRights
        constitution --> amendments
        constitution --> federalStatutes
        constitution --> caselaw
        constitution --> stateLaw
        constitution --> international
    `;

    // Level 2 - Federal Statutes categories
    if (expandedNodes.federalStatutes) {
      diagram += `
        federalStatutes --> civilRights[⊞ Civil Rights]
        federalStatutes --> criminal[⊞ Criminal Law]
        federalStatutes --> commerce[⊞ Commerce &<br/>Business]
        federalStatutes --> labor[⊞ Labor &<br/>Employment]
        federalStatutes --> ip[⊞ Intellectual<br/>Property]
        federalStatutes --> environmental[⊞ Environmental]
        federalStatutes --> healthcare[⊞ Healthcare]
        federalStatutes --> financial[⊞ Financial &<br/>Banking]
        federalStatutes --> adminLaw[⊞ Administrative<br/>Law]
      `;
    }

    // Level 2 - Case Law
    if (expandedNodes.caselaw) {
      diagram += `
        caselaw --> supremeCourt[Supreme Court]
        caselaw --> circuitCourts[Circuit Courts]
        caselaw --> districtCourts[District Courts]
      `;
    }

    // Level 2 - State Law
    if (expandedNodes.stateLaw) {
      diagram += `
        stateLaw --> stateConstitutions[State<br/>Constitutions]
        stateLaw --> stateStatutes[State Statutes]
        stateLaw --> uniformCodes[Uniform Codes]
      `;
    }

    diagram += `
    `;

    // Add expanded nodes - Level 2
    if (expandedNodes.civilRights) {
      diagram += `
        civilRights --> civilRightsAct1964[⊞ Civil Rights<br/>Act 1964]
        civilRights --> votingRights1965[Voting Rights<br/>Act 1965]
        civilRights --> fairHousing[Fair Housing<br/>Act 1968]
        civilRights --> ada[⊞ ADA 1990]
      `;
    }

    // Level 3 - Civil Rights Act 1964
    if (expandedNodes.civilRightsAct1964) {
      diagram += `
        civilRightsAct1964 --> civilRightsTitleII[Title II<br/>Public Accommodations]
        civilRightsAct1964 --> civilRightsTitleVII[Title VII<br/>Employment]
        civilRightsAct1964 --> civilRightsTitleVI[Title VI<br/>Federal Funding]
      `;
    }

    // Level 3 - ADA
    if (expandedNodes.ada) {
      diagram += `
        ada --> adaTitleI[Title I<br/>Employment]
        ada --> adaTitleII[Title II<br/>Public Services]
        ada --> adaTitleIII[Title III<br/>Public Accommodations]
      `;
    }

    if (expandedNodes.criminal) {
      diagram += `
        criminal --> title18[Title 18<br/>U.S.C.]
        criminal --> rico[RICO 1970]
        criminal --> patriotAct[⊞ PATRIOT<br/>Act 2001]
        criminal --> cfaa[Computer Fraud<br/>& Abuse]
      `;
    }

    // Level 3 - PATRIOT Act
    if (expandedNodes.patriotAct) {
      diagram += `
        patriotAct --> patriotActSurveillance[Section 215<br/>Business Records]
        patriotAct --> patriotActWiretaps[Section 206<br/>Roving Wiretaps]
        patriotAct --> patriotActIntelligence[Section 218<br/>FISA Intelligence]
      `;
    }

    if (expandedNodes.commerce) {
      diagram += `
        commerce --> shermanAct[Sherman Act<br/>1890]
        commerce --> claytonAct[Clayton Act<br/>1914]
        commerce --> sarbanesOxley[⊞ Sarbanes-Oxley<br/>2002]
        commerce --> doddFrank[⊞ Dodd-Frank<br/>2010]
      `;
    }

    // Level 3 - Sarbanes-Oxley
    if (expandedNodes.sarbanesOxley) {
      diagram += `
        sarbanesOxley --> sox302[Section 302<br/>CEO/CFO Cert]
        sarbanesOxley --> sox404[Section 404<br/>Internal Controls]
        sarbanesOxley --> sox806[Section 806<br/>Whistleblower]
      `;
    }

    // Level 3 - Dodd-Frank
    if (expandedNodes.doddFrank) {
      diagram += `
        doddFrank --> volckerRule[Section 619<br/>Volcker Rule]
        doddFrank --> cfpbCreation[Title X<br/>CFPB]
        doddFrank --> doddFrankSystemic[Title I<br/>Systemic Risk]
      `;
    }

    if (expandedNodes.labor) {
      diagram += `
        labor --> flsa[FLSA 1938]
        labor --> osha[OSHA 1970]
        labor --> fmla[FMLA 1993]
        labor --> erisa[ERISA 1974]
      `;
    }

    if (expandedNodes.ip) {
      diagram += `
        ip --> patents[Patent Act<br/>35 U.S.C.]
        ip --> copyrights[Copyright Act<br/>17 U.S.C.]
        ip --> trademarks[Trademark Act<br/>Lanham]
        ip --> dmca[DMCA 1998]
      `;
    }

    if (expandedNodes.environmental) {
      diagram += `
        environmental --> cleanAir[⊞ Clean Air<br/>Act 1970]
        environmental --> cleanWater[Clean Water<br/>Act 1972]
        environmental --> nepa[NEPA 1969]
        environmental --> esa[Endangered<br/>Species 1973]
      `;
    }

    // Level 3 - Clean Air Act
    if (expandedNodes.cleanAir) {
      diagram += `
        cleanAir --> naaqs[NAAQS<br/>Air Quality Standards]
        cleanAir --> newSourceReview[New Source<br/>Review]
        cleanAir --> titleVPermits[Title V<br/>Operating Permits]
      `;
    }

    if (expandedNodes.healthcare) {
      diagram += `
        healthcare --> medicare[Medicare<br/>1965]
        healthcare --> medicaid[Medicaid<br/>1965]
        healthcare --> hipaa[⊞ HIPAA 1996]
        healthcare --> aca[⊞ ACA 2010]
      `;
    }

    // Level 3 - HIPAA
    if (expandedNodes.hipaa) {
      diagram += `
        hipaa --> hipaaPrivacy[Privacy Rule<br/>45 CFR 160/164]
        hipaa --> hipaaSecurity[Security Rule<br/>45 CFR 164]
        hipaa --> hipaaBreach[Breach<br/>Notification]
      `;
    }

    // Level 3 - ACA
    if (expandedNodes.aca) {
      diagram += `
        aca --> acaIndividualMandate[Individual Mandate<br/>Section 1501]
        aca --> acaExchanges[Health Insurance<br/>Exchanges]
        aca --> acaEssentialBenefits[Essential Health<br/>Benefits]
      `;
    }

    if (expandedNodes.financial) {
      diagram += `
        financial --> securities1933[Securities<br/>Act 1933]
        financial --> exchange1934[Exchange<br/>Act 1934]
        financial --> fedReserve[Federal<br/>Reserve 1913]
        financial --> investmentCompany[Investment<br/>Company 1940]
      `;
    }

    if (expandedNodes.adminLaw) {
      diagram += `
        adminLaw --> cfr[Code of Federal<br/>Regulations]
        adminLaw --> epaRegs[EPA<br/>Regulations]
        adminLaw --> fdaRegs[FDA<br/>Regulations]
        adminLaw --> secRegs[SEC<br/>Regulations]
      `;
    }

    diagram += `
      classDef constitutionClass fill:#ff6b6b,stroke:#c92a2a,color:#fff,stroke-width:3px
      classDef statuteClass fill:#4ecdc4,stroke:#0b7285,color:#fff,stroke-width:2px
      classDef caseClass fill:#95e1d3,stroke:#087f5b,color:#333,stroke-width:2px
      classDef stateClass fill:#f9ca24,stroke:#f59f00,color:#333,stroke-width:2px
      classDef adminClass fill:#6c5ce7,stroke:#5f3dc4,color:#fff,stroke-width:2px
      classDef intlClass fill:#a29bfe,stroke:#7950f2,color:#fff,stroke-width:2px
      classDef expandableClass fill:#4ecdc4,stroke:#0b7285,color:#fff,stroke-width:3px,stroke-dasharray: 5 5
      classDef expandableCaseClass fill:#95e1d3,stroke:#087f5b,color:#333,stroke-width:3px,stroke-dasharray: 5 5
      classDef expandableStateClass fill:#f9ca24,stroke:#f59f00,color:#333,stroke-width:3px,stroke-dasharray: 5 5

      class constitution,billOfRights,amendments constitutionClass
      class federalStatutes expandableClass
      class civilRights,criminal,commerce,labor,ip,environmental,healthcare,financial expandableClass
      class civilRightsAct1964,ada,patriotAct,sarbanesOxley,doddFrank,cleanAir,hipaa,aca expandableClass
      class votingRights1965,fairHousing,title18,rico,cfaa,shermanAct,claytonAct,flsa,osha,fmla,erisa,patents,copyrights,trademarks,dmca,cleanWater,nepa,esa,medicare,medicaid,securities1933,exchange1934,fedReserve,investmentCompany statuteClass
      class civilRightsTitleII,civilRightsTitleVII,civilRightsTitleVI,adaTitleI,adaTitleII,adaTitleIII,patriotActSurveillance,patriotActWiretaps,patriotActIntelligence,sox302,sox404,sox806,volckerRule,cfpbCreation,doddFrankSystemic,naaqs,newSourceReview,titleVPermits,hipaaPrivacy,hipaaSecurity,hipaaBreach,acaIndividualMandate,acaExchanges,acaEssentialBenefits statuteClass
      class caselaw expandableCaseClass
      class supremeCourt,circuitCourts,districtCourts caseClass
      class stateLaw expandableStateClass
      class stateConstitutions,stateStatutes,uniformCodes stateClass
      class adminLaw expandableClass
      class cfr,epaRegs,fdaRegs,secRegs adminClass
      class international intlClass
    `;

    return diagram;
  }, [expandedNodes]);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      theme: 'base',
      themeVariables: {
        primaryColor: '#667eea',
        primaryTextColor: '#fff',
        primaryBorderColor: '#764ba2',
        lineColor: '#a29bfe',
        secondaryColor: '#95e1d3',
        tertiaryColor: '#f9ca24',
        fontSize: '13px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      },
      flowchart: {
        htmlLabels: true,
        curve: 'basis',
        padding: 12,
        nodeSpacing: 50,
        rankSpacing: 50
      }
    });

    const renderDiagram = async () => {
      if (mermaidRef.current) {
        mermaidRef.current.innerHTML = '';
        const diagramDefinition = generateMermaidDiagram();
        const { svg } = await mermaid.render('legalDiagram', diagramDefinition);
        mermaidRef.current.innerHTML = svg;

        // Add event listeners
        const nodes = mermaidRef.current.querySelectorAll('.node');
        nodes.forEach(node => {
          const nodeId = node.getAttribute('id');
          const cleanId = nodeId?.replace('flowchart-', '').replace(/-\d+$/, '');

          if (cleanId && legalNodeData[cleanId]) {
            node.style.cursor = 'pointer';

            // Hover for tooltip
            node.addEventListener('mouseenter', (e) => {
              const rect = node.getBoundingClientRect();
              setTooltipPosition({
                x: rect.left + rect.width / 2,
                y: rect.top
              });
              setSelectedNode(cleanId);
            });

            node.addEventListener('mouseleave', () => {
              setSelectedNode(null);
            });

            // Click for expand/collapse
            if (legalNodeData[cleanId].expandable) {
              node.addEventListener('click', (e) => {
                e.stopPropagation();
                toggleNode(cleanId);
              });
            }
          }
        });
      }
    };

    renderDiagram();
  }, [expandedNodes, generateMermaidDiagram, toggleNode]);

  return (
    <div className="interactive-legal-diagram">
      <div className="diagram-header">
        <h1>Interactive U.S. Legal System Hierarchy</h1>
        <p className="diagram-subtitle">
          Hover for details • Click ⊞ nodes to expand
        </p>
      </div>

      <div className="diagram-legend">
        <h3>Legend</h3>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color constitution-color"></span>
            <span>Constitutional</span>
          </div>
          <div className="legend-item">
            <span className="legend-color statute-color"></span>
            <span>Statutes</span>
          </div>
          <div className="legend-item">
            <span className="legend-color case-color"></span>
            <span>Case Law</span>
          </div>
          <div className="legend-item">
            <span className="legend-color state-color"></span>
            <span>State Law</span>
          </div>
          <div className="legend-item">
            <span className="legend-color admin-color"></span>
            <span>Administrative</span>
          </div>
          <div className="legend-item">
            <span className="legend-color intl-color"></span>
            <span>International</span>
          </div>
          <div className="legend-item">
            <span className="legend-icon">⊞</span>
            <span>Click to expand</span>
          </div>
        </div>
      </div>

      <div className="diagram-container">
        <div ref={mermaidRef} className="mermaid-diagram"></div>
      </div>

      {selectedNode && legalNodeData[selectedNode] && (
        <div
          className="node-tooltip compact"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 10}px`,
          }}
        >
          <div className="tooltip-content">
            <h4>{legalNodeData[selectedNode].title}</h4>
            <p>{legalNodeData[selectedNode].description}</p>
            <ul>
              {legalNodeData[selectedNode].examples.map((example, idx) => (
                <li key={idx}>{example}</li>
              ))}
            </ul>
          </div>
          <div className="tooltip-arrow"></div>
        </div>
      )}
    </div>
  );
}

export default InteractiveLegalDiagram;
